(() => {
  if (customElements.get('shopde-digital-contact')) return;
  class DigitalServicesContact extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.attachGuards();
        return;
      }
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.initialized = true;
      this.form.noValidate = false;
      this.summary = this.querySelector('[data-ds-errors]');
      this.list = this.querySelector('[data-ds-error-list]');
      this.scope = this.querySelector('[data-ds-field="project_scope"]');
      this.services = [...this.querySelectorAll('[data-ds-service]')];
      this.guardClick = (event) => {
        const button = event.target instanceof Element ? event.target.closest('button, input') : null;
        if (button?.form === this.form && (button.type === 'submit' || button.type === 'image')) this.validate(event);
      };
      this.guardSubmit = (event) => {
        if (event.target === this.form) this.validate(event);
      };
      this.attachGuards();
      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[data-ds-service]')) this.suggestScope();
        this.syncGroups();
        this.clearFieldError(event.target);
        this.refreshValidity();
      });
      this.form.addEventListener('input', (event) => {
        this.clearFieldError(event.target);
        this.refreshValidity();
      });
      this.form.addEventListener('invalid', (event) => {
        event.preventDefault();
        if (this.invalidScheduled) return;
        this.invalidScheduled = true;
        queueMicrotask(() => {
          this.invalidScheduled = false;
          if (this.isConnected) this.validate();
        });
      }, true);
      this.summary.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-ds-error-target]');
        if (!link) return;
        const field = this.form.querySelector(`[id="${CSS.escape(link.dataset.dsErrorTarget)}"]`);
        if (field) {
          event.preventDefault();
          field.focus();
          field.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      });
      this.refreshValidity();
      const status = this.querySelector('[data-ds-server-errors], [data-ds-submitted]');
      if (status) {
        status.focus({ preventScroll: true });
        status.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    }
    attachGuards() {
      if (this.guardsAttached) return;
      // Window capture runs before document/form handlers, including async submit integrations.
      // Only this form is guarded; valid events continue through the native Shopify flow.
      window.addEventListener('click', this.guardClick, true);
      window.addEventListener('submit', this.guardSubmit, true);
      this.guardsAttached = true;
    }
    disconnectedCallback() {
      window.removeEventListener('click', this.guardClick, true);
      window.removeEventListener('submit', this.guardSubmit, true);
      this.guardsAttached = false;
    }
    refreshValidity() {
      this.collectErrors();
    }
    suggestScope() {
      const contexts = new Set(this.services.filter((field) => field.checked && field.dataset.dsServiceContext).map((field) => field.dataset.dsServiceContext));
      if (contexts.size > 1) this.setScope('both_projects');
      else if (!this.scope.value && contexts.size === 1) this.setScope([...contexts][0]);
    }
    scopeCode() {
      return this.scope.selectedOptions[0]?.dataset.dsOption || '';
    }
    setScope(code) {
      const option = [...this.scope.options].find((item) => item.dataset.dsOption === code);
      if (option) this.scope.value = option.value;
    }
    setGroup(group, active) {
      group.hidden = !active;
      group.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = !active;
        if (field.hasAttribute('data-ds-conditional-required')) field.required = active;
        if (!active) this.clearFieldError(field);
      });
    }
    syncGroups() {
      const scope = this.scopeCode();
      const showNew = scope === 'new_project' || scope === 'both_projects';
      const showExisting = scope === 'existing_project' || scope === 'both_projects';
      this.setGroup(this.querySelector('[data-ds-project-group="new"]'), showNew);
      this.setGroup(this.querySelector('[data-ds-project-group="existing"]'), showExisting);
      const newShop = this.services.some((field) => field.checked && field.dataset.dsService === 'new_shop');
      this.setGroup(this.querySelector('[data-ds-shop-fields]'), showNew && newShop);
    }
    clearFieldError(field) {
      if (!(field instanceof HTMLElement)) return;
      field.removeAttribute('aria-invalid');
      if (typeof field.setCustomValidity === 'function') field.setCustomValidity('');
    }
    germanPhonePlausible(value) {
      if (!window.libphonenumber?.parsePhoneNumberFromString) return null;
      let input = value.trim();
      if (input.startsWith('0049')) input = `+49${input.slice(4)}`;
      try {
        const phone = window.libphonenumber.parsePhoneNumberFromString(input, { defaultCountry: 'DE', extract: false });
        return Boolean(phone && phone.country === 'DE' && phone.isValid());
      } catch {
        return false;
      }
    }
    collectErrors() {
      this.syncGroups();
      const errors = [];
      const addError = (field, message) => {
        if (errors.some((error) => error.field === field)) return;
        errors.push({ field, message });
      };
      const controls = [...this.form.querySelectorAll('[data-ds-field], [data-ds-service], [data-ds-privacy]')].filter((field) => !field.disabled);
      controls.forEach((field) => field.setCustomValidity(''));
      controls.forEach((field) => {
        const label = field.dataset.dsLabel;
        if (field.required && (field.type === 'checkbox' ? !field.checked : !field.value.trim())) {
          addError(field, field.hasAttribute('data-ds-privacy') ? this.dataset.errorPrivacy : `${label}: ${this.dataset.errorRequired}`);
        } else if (field.type === 'email' && field.value && field.validity.typeMismatch) {
          addError(field, this.dataset.errorEmail);
        } else if (field.dataset.dsField === 'postal_code' && !/^\d{5}$/.test(field.value.trim())) {
          addError(field, this.dataset.errorPostal);
        } else if (field.type === 'tel' && field.value) {
          const valid = this.germanPhonePlausible(field.value);
          if (valid !== true) addError(field, valid === null ? this.dataset.errorPhoneUnavailable : this.dataset.errorPhone);
        } else if (field.type === 'url' && field.value && (!/^https?:\/\//i.test(field.value) || field.validity.typeMismatch)) {
          addError(field, this.dataset.errorUrl);
        } else if (!field.validity.valid) {
          addError(field, `${label}: ${field.validationMessage}`);
        }
      });
      if (!this.services.some((field) => field.checked)) addError(this.services[0], this.dataset.errorServices);
      const selectedContexts = new Set(this.services.filter((field) => field.checked && field.dataset.dsServiceContext).map((field) => field.dataset.dsServiceContext));
      const projectScope = this.scopeCode();
      if (projectScope && projectScope !== 'both_projects' && [...selectedContexts].some((scope) => scope !== projectScope)) addError(this.scope, this.dataset.errorScope);
      errors.forEach(({ field, message }) => field.setCustomValidity(message));
      return errors;
    }
    validate(event) {
      const errors = this.collectErrors();
      this.form.querySelectorAll('[data-ds-field][aria-invalid], [data-ds-service][aria-invalid], [data-ds-privacy][aria-invalid]').forEach((field) => field.removeAttribute('aria-invalid'));
      this.list.replaceChildren();
      this.summary.hidden = errors.length === 0;
      if (!errors.length) return true;
      event?.preventDefault();
      event?.stopImmediatePropagation?.();
      errors.forEach(({ field, message }) => {
        field.setAttribute('aria-invalid', 'true');
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${field.id}`;
        link.dataset.dsErrorTarget = field.id;
        link.textContent = message;
        item.append(link);
        this.list.append(item);
      });
      this.summary.focus();
      this.summary.scrollIntoView({ block: 'center', behavior: 'auto' });
      return false;
    }
  }
  customElements.define('shopde-digital-contact', DigitalServicesContact);
})();
